import { PARAMS } from "@src/common/constants";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { TLShapeId } from "tldraw";
import { getBoardEventsEmitter } from "../../board/board-events-context";
import {
	CardDocumentId,
	PreprintDocumentId,
	fromDocumentIdString,
	isDocumentId,
} from "../../collaboration/document-id";
import {
	FOCUS_MENTION_ANCHOR_EVENT,
	type FocusMentionAnchorEventDetail,
	type MentionFocusCardId,
	mentionFocusCardTypeSchema,
} from "../constants";
import { useMarkMentionAsRead } from "../mutations/mark-as-read";
import type { MentionInboxItem } from "../schema";

function mentionCardId(sourceId: string): MentionFocusCardId | null {
	if (!isDocumentId(sourceId)) {
		return null;
	}
	const parsed = fromDocumentIdString(sourceId);
	if (parsed instanceof CardDocumentId) {
		return { id: parsed.id, type: mentionFocusCardTypeSchema.enum.card };
	}
	if (parsed instanceof PreprintDocumentId) {
		return {
			id: parsed.id,
			type: mentionFocusCardTypeSchema.enum.preprint,
		};
	}
	return null;
}

export function focusMentionTarget(
	item: MentionInboxItem,
	pathWithSearch: string,
) {
	try {
		const url = new URL(pathWithSearch, window.location.origin);
		const shapeId = url.searchParams.get(PARAMS.shapeId);
		const anchorId =
			url.searchParams.get(PARAMS.anchorId) ?? item.anchorId ?? null;

		if (anchorId) {
			window.dispatchEvent(
				new CustomEvent<FocusMentionAnchorEventDetail>(
					FOCUS_MENTION_ANCHOR_EVENT,
					{
						detail: {
							anchorId,
							cardId: mentionCardId(item.sourceId),
						},
					},
				),
			);
		}

		const emit = getBoardEventsEmitter();

		const hasCommentDeepLink =
			url.searchParams.has("comment") ||
			url.searchParams.has("reply") ||
			url.hash.includes("comment=");

		if (shapeId && !hasCommentDeepLink) {
			emit({
				type: "open-card-drawer",
				payload: {
					shapeId: shapeId as TLShapeId,
					trigger: "url_deep_link",
				},
			});
		}

		if (item.snippet) {
			emit({
				type: "set-text-cursor-position",
				payload: {
					cardId: item.sourceId,
					query: item.snippet.replace(/^@\S+\s*/, "").slice(0, 80),
				},
			});
		}

		if (anchorId) {
			requestAnimationFrame(() => {
				const el = document.querySelector(
					`[data-id="${CSS.escape(anchorId)}"]`,
				);
				el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
			});
		}
	} catch {
		// Board events unavailable outside a whiteboard session.
	}
}

export function useMentionDeepLink() {
	const navigate = useNavigate();
	const { mutate: markAsRead } = useMarkMentionAsRead();

	const handleNavigate = useCallback(
		(item: MentionInboxItem) => {
			if (!item.isRead) {
				markAsRead(item.id);
			}

			if (!item.deepLink) {
				return;
			}

			if (
				item.deepLink.startsWith("http://") ||
				item.deepLink.startsWith("https://")
			) {
				try {
					const url = new URL(item.deepLink);
					if (url.origin === window.location.origin) {
						const path = `${url.pathname}${url.search}${url.hash}`;
						navigate(path);
						focusMentionTarget(item, path);
					} else {
						window.location.href = item.deepLink;
					}
				} catch {
					navigate(item.deepLink);
				}
				return;
			}

			navigate(item.deepLink);
			focusMentionTarget(item, item.deepLink);
		},
		[markAsRead, navigate],
	);

	return handleNavigate;
}
